import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { X, Lock } from 'lucide-react';
import axios from 'axios';
import { formatDisplayPrice } from '../utils/currencyDisplay';
import { Button } from '../components/ui';
import BrandLogo from '../components/BrandLogo';

// Helper pour générer les URL d'API
const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' 
    ? 'https://website-alturion-production.up.railway.app' 
    : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

const FakeStripeCheckoutScreen = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  
  const cartItems = useMemo(() => location.state?.cartItems || [], [location.state?.cartItems]);
  
  useEffect(() => {
    if (!userInfo) {
      navigate('/login');
      return;
    }
    
    if (!cartItems || cartItems.length === 0) {
      navigate('/cart');
    }
  }, [userInfo, cartItems, navigate]);

  const processPayment = async () => {
    setProcessing(true);
    setProgress(0);
    setCurrentStep(t('checkout.processing.verifying'));
    
    const steps = [
      { text: t('checkout.processing.verifying'), duration: 800 },
      { text: t('checkout.processing.validating'), duration: 1000 },
      { text: t('checkout.processing.processing'), duration: 1200 },
      { text: t('checkout.processing.finalizing'), duration: 600 }
    ];
    
    let totalProgress = 0;
    
    for (let i = 0; i < steps.length; i++) {
      setCurrentStep(steps[i].text);
      
      const stepProgress = 100 / steps.length;
      const startProgress = totalProgress;
      const endProgress = totalProgress + stepProgress;
      
      const startTime = Date.now();
      const duration = steps[i].duration;
      
      while (Date.now() - startTime < duration) {
        const elapsed = Date.now() - startTime;
        const stepCompletion = Math.min(elapsed / duration, 1);
        const currentProgress = startProgress + (stepProgress * stepCompletion);
        setProgress(currentProgress);
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      
      totalProgress = endProgress;
      setProgress(totalProgress);
    }
    
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userInfo.token}`,
        },
      };

      const { data } = await axios.post(
        getApiUrl('/api/checkout/create-free-order'),
        { cartItems },
        config
      );

      if (data.redirectUrl) {
        window.location.href = data.redirectUrl;
      } else {
        navigate(`/checkout/success?order_id=${data.orderId}&free=true`);
      }
    } catch (error) {
      console.error('Erreur lors de la création de la commande gratuite:', error);
      navigate('/cart');
    }
  };
  
  const total = cartItems.reduce((acc, item) => acc + (item.price * item.qty), 0);

  return (
    <div className="min-h-screen bg-bg px-6 pt-28 pb-24 md:pt-32">
      <motion.div
        className="mx-auto w-full max-w-md"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="mb-8 flex items-center justify-between border-b border-white/[.06] pb-5">
          <BrandLogo link={false} className="h-8" />
          <button
            type="button"
            onClick={() => navigate('/cart')}
            className="text-muted transition-colors hover:text-body"
            disabled={processing}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {!processing ? (
          <>
            <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.16em] text-muted">
              Checkout
            </p>
            <h1 className="mb-8 text-[28px] font-semibold tracking-tight text-body">
              {t('checkout.orderSummary')}
            </h1>

            <div className="divide-y divide-white/[.06] border-y border-white/[.06]">
              {cartItems.map((item) => (
                <div key={item.product} className="flex items-center justify-between gap-4 py-4">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-body">{item.name}</p>
                    <p className="mt-0.5 text-xs text-muted">
                      {t('checkout.quantity')}: {item.qty}
                    </p>
                  </div>
                  <p className="shrink-0 text-sm font-medium text-body">
                    {item.price === 0
                      ? t('checkout.free')
                      : formatDisplayPrice(item.price, i18n.language)}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex items-baseline justify-between gap-4">
              <span className="text-sm text-muted">{t('checkout.total')}</span>
              <span className="text-xl font-semibold tracking-tight text-body">
                {total === 0 ? t('checkout.free') : formatDisplayPrice(total, i18n.language)}
              </span>
            </div>

            {total === 0 && (
              <p className="mt-4 text-sm text-muted">{t('checkout.freeProducts')}</p>
            )}

            <Button onClick={processPayment} className="mt-8 w-full py-3">
              {total === 0
                ? t('checkout.confirmFreeOrder')
                : t('checkout.pay', { price: formatDisplayPrice(total, i18n.language) })}
            </Button>

            <div className="mt-5 flex items-center justify-center gap-1.5 text-xs text-muted">
              <Lock className="h-3.5 w-3.5" aria-hidden="true" />
              {t('checkout.securePayment')}
            </div>
          </>
        ) : (
          <div className="py-10 text-center">
            <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.16em] text-muted">
              Checkout
            </p>
            <h2 className="text-xl font-semibold tracking-tight text-body">
              {t('checkout.processing.title')}
            </h2>
            <p className="mt-2 text-sm text-muted">{currentStep}</p>

            <div className="mx-auto mt-8 h-px w-full max-w-xs overflow-hidden bg-white/[.08]">
              <div
                className="h-full bg-body transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="mt-3 text-xs text-muted">
              {Math.round(progress)}% {t('checkout.processing.completed')}
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default FakeStripeCheckoutScreen;
